import React, { useState, useEffect } from 'react';
import {
  isSafari,
  isIos,
  isInstalled,
  shouldShowBanner,
  markShown,
  markDismissed,
  markInstalled,
} from '../services/install';
import './InstallBanner.css';

/**
 * Prompt to install the app.
 *
 * Chromium gets a real install dialog via the deferred beforeinstallprompt
 * event. Safari has no such API, so it gets the Share → Add to Home Screen
 * instruction instead, shown only once.
 */
function InstallBanner() {
  const [prompt, setPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isInstalled()) return undefined;

    const onBeforeInstall = (e) => {
      // Stop the browser's own mini-infobar so ours is the only prompt.
      e.preventDefault();
      setPrompt(e);
      if (shouldShowBanner(true)) {
        setVisible(true);
        markShown();
      }
    };

    const onInstalled = () => {
      markInstalled();
      setVisible(false);
      setPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    // Safari never fires the event, so decide without waiting for it. The
    // delay keeps the banner from competing with the first paint.
    let timer;
    if (isSafari() || isIos()) {
      timer = setTimeout(() => {
        if (shouldShowBanner(false)) {
          setVisible(true);
          markShown();
        }
      }, 2500);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const install = async () => {
    if (!prompt) return;
    prompt.prompt();
    try {
      const { outcome } = await prompt.userChoice;
      if (outcome === 'accepted') markInstalled();
      else markDismissed();
    } catch (e) {
      markDismissed();
    }
    setVisible(false);
    setPrompt(null);
  };

  const dismiss = () => {
    markDismissed();
    setVisible(false);
  };

  if (!visible) return null;

  const manual = isSafari() || isIos();

  return (
    <div className="install-banner" role="region" aria-label="Install Astrobot">
      <span className="install-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path
            d="M12 3v11m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>

      <div className="install-text">
        <strong>Install Astrobot</strong>
        {manual ? (
          <span>
            Tap the Share button, then <em>Add to Home Screen</em>.
          </span>
        ) : (
          <span>Keep your charts one tap away, and read them offline.</span>
        )}
      </div>

      {!manual && (
        <button type="button" className="install-btn" onClick={install}>
          Install
        </button>
      )}

      <button
        type="button"
        className="install-dismiss"
        onClick={dismiss}
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
}

export default InstallBanner;
